import { GuestInfo } from '@models/guest-info.model';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from '@services/api/api.service';
import { BookingClientService } from '@services/api/booking/booking-client.service';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { BrevoService } from '@services/messaging/brevo.service';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';

export interface CommunicationRequest {
  useCase: string;
  channel: 'email' | 'sms';
  language: string;
  recipient: string;
}

export interface MessagePreview {
  text: string;
  link?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CommunicationModalService {
  private readonly bookingClientService = inject(BookingClientService);
  private readonly apiService = inject(ApiService);
  private readonly brevoService = inject(BrevoService);
  private readonly translateService = inject(TranslateService);

  /**
   * Gets the flat number and entry code info from a booking and entry codes array
   * If multiple flats exist, returns the first one's information
   * Uses entry codes passed from component if available, otherwise falls back to booking data
   */
  private getFlatAndEntryCodeInfo(booking?: IBookingModel | null, entryCodes?: string[]): { flatNumber: string; entryCode: string } {
    // Default placeholder values
    let flatNumber = 'FLAT_NUMBER';
    let entryCode = 'ENTRY_CODE';

    // First try to use passed entry codes if available
    if (entryCodes && entryCodes.length > 0) {
      entryCode = entryCodes[0];
    }

    // Get flat number from booking data
    if (booking?.flats && booking.flats.length > 0) {
      // Use the first flat's information if available
      const flat = booking.flats[0];
      if (flat.flat_number) {
        flatNumber = flat.flat_number;
      }

      // Only fall back to booking entry codes if no external ones were provided
      if (!entryCodes || entryCodes.length === 0) {
        if (flat.entryCodes && flat.entryCodes.length > 0) {
          entryCode = flat.entryCodes[0];
        }
      }
    }

    return { flatNumber, entryCode };
  }

  /**
   * Generates message preview based on use case, channel, and language
   */
  getMessagePreview(
    useCase: string,
    channel: 'email' | 'sms',
    language: string,
    booking?: IBookingModel | null,
    entryCodes?: string[]
  ): MessagePreview {
    switch (useCase) {
      case 'entry-code':
        if (channel === 'email') {
          // Get flat number and entry code from booking data and passed entry codes
          const { flatNumber, entryCode } = this.getFlatAndEntryCodeInfo(booking, entryCodes);
          const guestName = booking?.user?.name || booking?.name || '';
          const hasParking = booking?.flats?.some((flat) => flat.flatType?.id === 2) || false;

          if (language === 'hu') {
            return {
              text: `Kedves ${guestName || 'Vendégünk'}!

Köszönjük, hogy a SmartApartot választottad!

A SmartApart épületét${hasParking ? ', a parkolót' : ''} és az apartmanod ajtaját kulcs nélkül, a következő 4 számjegyű kóddal tudod kinyitni:

Szobaszám: ${flatNumber} belépési kód: ${entryCode}

(a szobaszám első számjegye azt az emeletet jelöli, ahol a szobád található)

A fenti kóddal az ajtókat az érkezésed napján 15:00 órától a távozásod napján 11:00 óráig fogod tudni nyitni!

A szálláshely nem rendelkezik 24 órás recepcióval.

Ha bármilyen segítségre lenne szükséged, akkor a következő e-mail címen állunk rendelkezésedre: host@smartapart.hu.

Ha szeretnél a szálláson parkolni azt kérlek jelezd felénk az érkezésed előtt emailben. Az őrzött parkolás díja napi 8 euró.

A házban található Wi-Fi hálózathoz csatlakozni az alábbi adatokkal tudsz:
SSID: smartapart
Jelszó: HelloGuest!

Kellemes pihenést és jó időtöltést kíván
a SmartApart csapata!`
            };
          } else {
            return {
              text: `Dear ${guestName || 'Guest'},

Thank you for booking with SmartApart!

SmartApart is a keyless building. To access the building${hasParking ? ', the parking lot' : ''} and your apartment, please use the following 4 digit access code:

Room number: ${flatNumber} entry code: ${entryCode}

(the first digit of your room number indicates the floor number of your room)

The code will be valid from 15.00 on the day of your arrival/check-in until 11.00 am on your departure/check-out date!

Please note that we do not have a 24 hour on site reception.

If you need any assistance please contact us at host@smartapart.hu.

If you require parking during your stay please let us know in advance by sending us an email. The cost of on site secure underground parking is 8 euros/day

Access details for the Wi-Fi network:
SSID: smartapart
Password: HelloGuest!

Have a pleasant stay with SmartApart!
the SmartApart Team`
            };
          }
        } else {
          // SMS preview with actual message content
          // Get flat number and entry code from booking data and passed entry codes
          const { flatNumber, entryCode } = this.getFlatAndEntryCodeInfo(booking, entryCodes);

          if (language === 'hu') {
            return {
              text: `KÉRJÜK, NE VALASZOLJON ERRE AZ ÜZENETRE!\nElérhetöségünk: +36 20 745 2829\n\nÜdvözöljük Budapesten!\n\nAz épületbe és az apartmanba bejutashoz hasznalhato kod:\n\n${flatNumber} lakas: ${entryCode}\n\nÜdv:\nSmartApart csapata`
            };
          } else {
            return {
              text: `PLEASE DO NOT REPLY\nfor assistance please contact us on +36 20 745 2829\n\nWelcome To Budapest!\nThe entry code for the SmartApart building and your apartman door is:\n\nFlat ${flatNumber}: ${entryCode}\n\nHave a pleasant stay!\nSmartApart team`
            };
          }
        }

      case 'checkin-link':
        const checkinLink = this.generateCheckinLink(booking);
        if (channel === 'email') {
          // Full email templates for check-in reminders
          const guestName = booking?.user?.name || booking?.name || '';

          if (language === 'hu') {
            return {
              text: `${guestName || 'Szia'}, ideje bejelentkezni!
Kedves ${guestName || 'Vendégünk'},
Üdvözlünk a SmartApartban! Hamarosan találkozunk, reméljük, hogy utazásod nagyszerű és felejthetetlen lesz!

Első lépésként szeretnénk kérni, hogy minél előbb töltsd ki egyszerű online bejelentkezési űrlapunkat. Ezzel a lépéssel mindent tökéletesen elő tudunk készíteni, így Te is zavartalanul élvezheted az itt töltött időt.

Kattints az alábbi linkre az online bejelentkezéshez:

Online bejelentkezés most!

Ne feledd, ha kérdésed van, vagy különleges kéréssel fordulnál hozzánk, bármikor állunk rendelkezésedre. Csapatunk folyamatosan azon dolgozik, hogy a nálunk eltöltött időd kényelmes és zavartalan legyen.

Köszönjük, hogy a SmartApartot választottad szálláshelyednek. Alig várjuk, hogy vendégül lássunk és emlékezetes pillanatokat teremtsünk számodra!

Üdvözlettel,
A SmartApart Csapata`,
              link: checkinLink
            };
          } else {
            return {
              text: `${guestName || 'Hi'}, it's time to check in!
Dear ${guestName || 'Guest'},
We're thrilled to welcome you to SmartApart and can't wait to make your stay absolutely fantastic!

To get the ball rolling and ensure a seamless check-in process, we invite you to complete our quick and easy check-in form right away. This step will help us prepare everything to perfection, so you can dive straight into an unforgettable experience.

Click the link below to access the check-in form:

Complete Check-In Now!

Rest assured, we're here to assist with any questions or special requests you may have. Our team is dedicated to ensuring your time with us is filled with comfort and delight.

Thank you for choosing SmartApart for your stay. We're looking forward to hosting you and creating incredible memories together!

See you soon!

Best regards,
The SmartApart Team`,
              link: checkinLink
            };
          }
        } else {
          // SMS preview with actual message content
          if (language === 'hu') {
            return {
              text: `Szia! A SmartAparttól írunk\nKöszi a foglalást! Kérjük, jelentkezz be előre, hogy gördülékeny legyen az érkezésed:\n${checkinLink}`,
              link: checkinLink
            };
          } else {
            return {
              text: `Hi, this is SmartApart Thanks for your booking!\nPlease complete your check-in to avoid delays:\n${checkinLink}`,
              link: checkinLink
            };
          }
        }

      case 'parking-link':
        const parkingLink = this.generateParkingLink(booking);
        if (channel === 'email') {
          // Email preview with actual template content
          const guestName = booking?.user?.name || booking?.name || '';
          if (language === 'hu') {
            return {
              text: `${guestName || 'Szia'}, foglalj parkolóhelyet tartózkodásod idejére!
Kedves ${guestName || 'Vendégünk'},
A tartózkodásod idejére az alábbi linken tudod lefoglalni parkolóhelyed:

Parkolóhely foglalása most
Ha nem jelenik meg a link, az alábbi webcímet másold át a böngészőbe a bejelentkezés megkezdéséhez: ${parkingLink}

Ne feledd, ha kérdésed van, vagy különleges kéréssel fordulnál hozzánk, bármikor állunk rendelkezésedre.

Köszönjük, hogy a SmartApartot választottad szálláshelyedül. Alig várjuk, hogy vendégül lássunk és emlékezetes pillanatokat teremtsünk számodra!

Üdvözlettel,
A SmartApart Csapata`,
              link: parkingLink
            };
          } else {
            return {
              text: `${guestName || 'Hi'}, here is your link to book your parking spot
Dear ${guestName || 'Guest'},
You can book your parking spot for your stay using the link below:

Book parking now
If the link does not appear, please copy and paste the following address into your browser: ${parkingLink}

If you have any questions or special requests, please don't hesitate to contact us anytime.

Thank you for choosing SmartApart as your accommodation. We look forward to hosting you and creating memorable moments for you!

Best regards,
SmartApart Team`,
              link: parkingLink
            };
          }
        } else {
          // SMS preview with actual message content
          if (language === 'hu') {
            return {
              text: `Kedves Vendégünk! Itt tud parkolót foglalni a szállodába: ${parkingLink}`,
              link: parkingLink
            };
          } else {
            return {
              text: `Dear Guest! You can book your parking here: ${parkingLink}`,
              link: parkingLink
            };
          }
        }

      default:
        return {
          text: 'Unknown message type.'
        };
    }
  }

  /**
   * Generates the check-in link for a booking
   */
  private generateCheckinLink(booking?: IBookingModel | null): string {
    if (!booking?.reservation_code) {
      return 'https://i11.hu/cin/RESERVATION_CODE';
    }

    const baseUrl = this.apiService.getBaseUrl();
    // Extract domain from API base URL and construct the check-in URL
    const domain = baseUrl.includes('teszt') ? 'teszt.i11.hu' : 'i11.hu';
    return `https://${domain}/cin/${booking.reservation_code}`;
  }

  /**
   * Generates the parking link for a booking
   */
  private generateParkingLink(booking?: IBookingModel | null): string {
    if (!booking?.reservation_code) {
      return 'https://i11.hu/hu/parking/RESERVATION_CODE';
    }

    const baseUrl = this.apiService.getBaseUrl();
    // Extract domain from API base URL and construct the parking URL
    const domain = baseUrl.includes('teszt') ? 'teszt.i11.hu' : 'i11.hu';
    return `https://${domain}/hu/parking/${booking.reservation_code}`;
  }

  /**
   * Checks if a use case + channel combination is implemented
   */
  isImplemented(useCase: string, channel: 'email' | 'sms'): boolean {
    // Implemented use cases:
    if (useCase === 'entry-code') {
      return true; // Both email and SMS
    }
    if (useCase === 'checkin-link') {
      return true; // Both email and SMS now implemented
    }
    if (useCase === 'parking-link') {
      return true; // Both email and SMS now implemented
    }
    return false;
  }

  /**
   * Checks if the language is determined by the server for a given use case + channel
   * Entry code (both), Checkin SMS, Parking SMS have server-decided language
   */
  isLanguageServerDecided(useCase: string, channel: 'email' | 'sms'): boolean {
    if (useCase === 'entry-code') {
      return true; // Both email and SMS have server-decided language
    }
    if (useCase === 'checkin-link' && channel === 'sms') {
      return true; // SMS language is server-decided
    }
    if (useCase === 'parking-link' && channel === 'sms') {
      return true; // SMS language is server-decided
    }
    return false;
  }

  /**
   * Gets info text for server-decided language fields
   */
  getLanguageInfoText(): string {
    return this.translateService.instant('communication.modal.info.languageServerDecided');
  }

  /**
   * Determines the language based on nationality
   * If nationality is HU, returns Hungarian, otherwise English
   */
  getLanguageFromNationality(nationality?: string): 'en' | 'hu' {
    return nationality === 'HU' ? 'hu' : 'en';
  }

  /**
   * Checks if the recipient should be readonly for a given use case + channel
   */
  isRecipientReadonly(useCase: string, channel: 'email' | 'sms'): boolean {
    // Entry code methods use booking ID only, so recipient is readonly
    return useCase === 'entry-code';
  }

  /**
   * Gets info text for readonly recipient fields
   */
  getRecipientInfoText(): string {
    return this.translateService.instant('communication.modal.info.recipientReadonly');
  }

  /**
   * Sends the communication message
   */
  sendMessage(request: CommunicationRequest, booking: IBookingModel, guestInfo: GuestInfo): Observable<any> {
    if (!this.isImplemented(request.useCase, request.channel)) {
      return throwError(() => new Error('This use case is not yet implemented.'));
    }

    const bookingId = booking.id;
    if (!bookingId) {
      return throwError(() => new Error('Booking ID is required.'));
    }

    switch (request.useCase) {
      case 'entry-code':
        if (request.channel === 'email') {
          return this.bookingClientService.sendEntryCodeEmail(bookingId).pipe(
            map(() => ({ success: true, message: this.translateService.instant('communication.success.entryCodeEmail') })),
            catchError((error) => throwError(() => new Error('Failed to send entry code email: ' + error.message)))
          );
        } else {
          return this.bookingClientService.sendEntryCodeSms(bookingId).pipe(
            map(() => ({ success: true, message: this.translateService.instant('communication.success.entryCodeSms') })),
            catchError((error) => throwError(() => new Error('Failed to send entry code SMS: ' + error.message)))
          );
        }

      case 'checkin-link':
        if (request.channel === 'sms') {
          return this.bookingClientService.sendCheckinSms(bookingId, request.recipient).pipe(
            map(() => ({ success: true, message: this.translateService.instant('communication.success.checkinSms') })),
            catchError((error) => throwError(() => new Error('Failed to send check-in SMS: ' + error.message)))
          );
        } else {
          // Check-in email using Brevo service
          const checkinUrl = this.generateCheckinLink(booking);
          const guestName = guestInfo.name || undefined;
          const language = request.language as 'en' | 'hu';

          return this.brevoService.sendCheckinEmail(request.recipient, guestName, checkinUrl, language).pipe(
            map(() => ({ success: true, message: this.translateService.instant('communication.success.checkinEmail') })),
            catchError((error) => throwError(() => new Error('Failed to send check-in email: ' + error.message)))
          );
        }

      case 'parking-link':
        if (request.channel === 'sms') {
          return this.bookingClientService.sendParkingSms(bookingId, request.recipient).pipe(
            map(() => ({ success: true, message: this.translateService.instant('communication.success.parkingSms') })),
            catchError((error) => throwError(() => new Error('Failed to send parking SMS: ' + error.message)))
          );
        } else {
          // Parking email using Brevo service
          const parkingUrl = this.generateParkingLink(booking);
          const guestName = guestInfo.name || undefined;
          const language = request.language as 'en' | 'hu';

          return this.brevoService.sendParkingEmail(request.recipient, guestName, parkingUrl, language).pipe(
            map(() => ({ success: true, message: this.translateService.instant('communication.success.parkingEmail') })),
            catchError((error) => throwError(() => new Error('Failed to send parking email: ' + error.message)))
          );
        }
    }

    return throwError(() => new Error('This use case + channel combination is not yet implemented.'));
  }
}
